/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * Component to collapse content
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.0.0
 */
import React from 'react';

import withDefaultProps from '../../../modules/app/hoc/withDefaultProps';
import { ICollapsePanelProps } from './CollapsePanel';
import { CollapseContainerStyled } from './styled/CollapseStyled';

/**
 * @description Collapse props for main container.
 * @interface ICollapseProps
 * @extends ICollapsePanelProps
 *
 * @property{string} containerStyle Define style props for collapse container.
 *
 * @see Collapse component For all panels.
 * @see CollapsePanel component For panels in collapse.
 * @see ICollapseProps interface Props for Collapse container.
 */
interface ICollapseProps extends ICollapsePanelProps {
    containerStyle: string;
}

// default props for Collapse component
const defaultProps: ICollapseProps = {
    bodyStyle: '',
    containerStyle: '',
    icon: '',
    iconSize: 32,
    isOpen: false,
    panelContainerStyle: '',
    titleStyle: '',
    titleValue: '',
};

/**
 * @description Component for render collapse container with panels.
 * @class Collapse stateless component
 * @extends Component<ICollapseProps>
 */
const Collapse: React.FC<ICollapseProps> = (props) => {

    const {
        bodyStyle,
        containerStyle,
        icon,
        iconSize,
        panelContainerStyle,
        titleStyle,
    } = props;

    // add props for panels
    const childrenWithProps = React.Children.map(props.children, (child: React.ReactNode, index: number) =>
        React.cloneElement(child as React.ReactElement,
            {
                bodyStyle,
                className: `collapse__panel collapse__panel-${index}`,
                icon,
                iconSize,
                key: index,
                panelContainerStyle,
                titleStyle,
            },
        ),
    );

    return (
        <CollapseContainerStyled styleProps={containerStyle}>
            {childrenWithProps}
        </CollapseContainerStyled>
    );

};

export default withDefaultProps(defaultProps, Collapse);
